import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useParams, useLocation, useOutletContext } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useThemeMode } from '../contexts/ThemeContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { supabase } from '../lib/supabase'
import type { Organization } from '../types/database'
import {
  VStack,
  HStack,
  Skeleton,
  AppSidebar,
  Avatar,
  Button,
  Dropdown,
  Text,
  Heading
} from 'wss3-forge'
import {
  Home20Regular,
  Globe20Regular,
  Key20Regular,
  People20Regular,
  Settings20Regular,
  SignOut20Regular,
  Person20Regular,
  WeatherSunny16Regular,
  WeatherMoon16Regular,
  Apps20Regular,
  DataUsage20Regular,
  History20Regular,
  Shield20Regular,
  Navigation20Regular,
  PlugConnected20Regular
} from '@fluentui/react-icons'

interface OrgContext {
  organization: Organization
  reloadOrganization: () => Promise<void>
}

export function OrgLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { slug } = useParams()
  const { user, signOut } = useAuth()
  const { mode, setMode } = useThemeMode()

  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userAvatar, setUserAvatar] = useState<string | undefined>()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (user?.id) {
      loadUserAvatar()
    }
  }, [user?.id])

  useEffect(() => {
    if (slug) {
      loadOrganization()
    }
  }, [slug])

  const loadUserAvatar = async () => {
    if (!user?.id) return
    const { data } = await supabase.rpc('get_user_email', { p_user_id: user.id })
    const userInfo = Array.isArray(data) ? data[0] : data
    if (userInfo?.avatar_url) {
      setUserAvatar(userInfo.avatar_url)
    }
  }

  const loadOrganization = async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase.rpc('get_my_organization', {
        p_slug: slug
      })

      if (rpcError) throw rpcError

      // Handle null, empty array, or object with null id
      const orgData = Array.isArray(data) ? data[0] : data
      if (!orgData || !orgData.id) {
        setError('Organization not found or access denied')
        setOrganization(null)
      } else {
        setOrganization(orgData)
      }
    } catch (err: any) {
      console.error('Error loading organization:', err)
      setError(err.message || 'Error loading organization')
    } finally {
      setLoading(false)
    }
  }

  const getActiveId = () => {
    const path = location.pathname
    if (path === `/org/${slug}`) return 'dashboard'
    if (path.includes('/apps')) return 'apps'
    if (path.includes('/domains')) return 'domains'
    if (path.includes('/sso')) return 'sso'
    if (path.includes('/integrations')) return 'integrations'
    if (path.includes('/members')) return 'members'
    if (path.includes('/analytics')) return 'analytics'
    if (path.includes('/audit')) return 'audit'
    if (path.includes('/settings')) return 'settings'
    return 'dashboard'
  }

  const getPageTitle = () => {
    const path = location.pathname
    if (path === `/org/${slug}`) return 'Dashboard'
    if (path.includes('/apps')) return 'Apps & Services'
    if (path.includes('/domains')) return 'Domains'
    if (path.includes('/sso')) return 'SSO Connections'
    if (path.includes('/integrations')) return 'Integrations'
    if (path.includes('/members')) return 'Members'
    if (path.includes('/analytics')) return 'Analytics'
    if (path.includes('/audit')) return 'Audit Logs'
    if (path.includes('/settings')) return 'Settings'
    return 'Dashboard'
  }

  // Set page title
  usePageTitle(
    organization ? getPageTitle() : undefined,
    organization ? `${organization.name} - Org Manager` : 'Org Manager'
  )

  const handleNavigate = (id: string) => {
    if (id === 'dashboard') navigate(`/org/${slug}`)
    else navigate(`/org/${slug}/${id}`)
    if (isMobile) setSidebarOpen(false)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  if (loading) {
    return (
      <HStack style={{ minHeight: '100vh' }}>
        <div style={{ width: '280px', padding: '1.5rem' }}>
          <Skeleton width={150} height={24} />
          <VStack gap="sm" style={{ marginTop: '2rem' }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} width="100%" height={40} />
            ))}
          </VStack>
        </div>
        <div style={{ flex: 1, padding: '2rem' }}>
          <Skeleton width={300} height={32} />
        </div>
      </HStack>
    )
  }

  if (error || !organization) {
    const isNotFound = error?.toLowerCase().includes('not found')
    const title = isNotFound ? 'Organization Not Found' : 'Access Denied'
    const message = isNotFound
      ? "The organization you're looking for doesn't exist or has been deleted."
      : "You don't have permission to access this organization's admin portal. Contact your organization administrator if you believe this is an error."

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-primary)',
        padding: '2rem'
      }}>
        <VStack gap="lg" style={{ alignItems: 'center', maxWidth: '400px', textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'var(--status-error-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Shield20Regular style={{ fontSize: '40px', color: 'var(--status-error)' }} />
          </div>
          <VStack gap="sm" style={{ alignItems: 'center' }}>
            <Heading level={3}>{title}</Heading>
            <Text color="muted" style={{ textAlign: 'center', lineHeight: 1.6 }}>
              {message}
            </Text>
          </VStack>
          <Button onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </VStack>
      </div>
    )
  }

  const sidebarSections = [
    {
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: <Home20Regular /> }
      ]
    },
    {
      title: 'Configuration',
      items: [
        { id: 'apps', label: 'Apps & Services', icon: <Apps20Regular /> },
        { id: 'domains', label: 'Domains', icon: <Globe20Regular /> },
        { id: 'sso', label: 'SSO Connections', icon: <Key20Regular /> },
        { id: 'integrations', label: 'Integrations', icon: <PlugConnected20Regular /> }
      ]
    },
    {
      title: 'Team',
      items: [
        { id: 'members', label: 'Members', icon: <People20Regular /> }
      ]
    },
    {
      title: 'Insights',
      items: [
        { id: 'analytics', label: 'Analytics', icon: <DataUsage20Regular /> },
        { id: 'audit', label: 'Audit Logs', icon: <History20Regular /> }
      ]
    }
  ]

  const bottomItems = [
    { id: 'settings', label: 'Settings', icon: <Settings20Regular /> }
  ]

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Mobile header */}
      {isMobile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1rem',
          zIndex: 99
        }}>
          <HStack gap="md" style={{ alignItems: 'center' }}>
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-primary)'
              }}
            >
              <Navigation20Regular />
            </button>
            <img
              src="/Logo_orgma.png"
              alt="Org Manager"
              height={28}
              style={{
                filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)',
                opacity: 0.9
              }}
            />
          </HStack>
          <Dropdown
            trigger={
              <div className="clickable-avatar">
                <Avatar name={user?.email || ''} src={userAvatar} size="sm" showTooltip={false} />
              </div>
            }
            items={[
              { id: 'account', label: 'My Account', icon: <Person20Regular />, onClick: () => navigate('/account') },
              { id: 'signout', label: 'Sign out', icon: <SignOut20Regular />, onClick: handleSignOut }
            ]}
          />
        </div>
      )}

      {/* Sidebar - inline on desktop, drawer on mobile */}
      {!isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100 }}>
          <AppSidebar
            mode="inline"
            logo={
              <img
                src="/Logo_orgma.png"
                alt="Org Manager"
                height={40}
                style={{
                  filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)',
                  opacity: 0.9
                }}
              />
            }
            sections={sidebarSections}
            activeId={getActiveId()}
            onNavigate={handleNavigate}
            showSearch={false}
            bottomItems={bottomItems}
            width={280}
          />
        </div>
      )}

      {isMobile && (
        <AppSidebar
          mode="drawer"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          logo={
            <img
              src="/Logo_orgma.png"
              alt="Org Manager"
              height={40}
              style={{
                filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)',
                opacity: 0.9
              }}
            />
          }
          sections={sidebarSections}
          activeId={getActiveId()}
          onNavigate={handleNavigate}
          showSearch={false}
          bottomItems={bottomItems}
          width={280}
        />
      )}

      {/* Desktop avatar dropdown */}
      {!isMobile && (
        <div style={{ position: 'fixed', top: '2rem', right: '2.5rem', zIndex: 100 }}>
          <Dropdown
            trigger={
              <div className="clickable-avatar">
                <Avatar name={user?.email || ''} src={userAvatar} size="sm" showTooltip={false} />
              </div>
            }
            items={[
              { id: 'account', label: 'My Account', icon: <Person20Regular />, onClick: () => navigate('/account') },
              { id: 'signout', label: 'Sign out', icon: <SignOut20Regular />, onClick: handleSignOut }
            ]}
          />
        </div>
      )}

      <main style={{
        marginLeft: isMobile ? 0 : 280,
        marginTop: isMobile ? '60px' : 0,
        minHeight: isMobile ? 'calc(100vh - 60px)' : '100vh',
        backgroundColor: 'var(--bg-primary)',
        overflow: 'auto'
      }}>
        <div className="page-content">
          <Outlet context={{ organization, reloadOrganization: loadOrganization }} />
        </div>
      </main>

      {/* Floating theme toggle */}
      <div
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          zIndex: 1000,
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '9999px',
          padding: '4px',
          display: 'flex',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}
      >
        <button
          onClick={() => setMode('light')}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '9999px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: mode === 'light' ? 'var(--brand-primary)' : 'transparent',
            color: mode === 'light' ? 'white' : 'var(--text-muted)',
            transition: 'all 0.2s ease'
          }}
        >
          <WeatherSunny16Regular />
        </button>
        <button
          onClick={() => setMode('dark')}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '9999px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: mode === 'dark' ? 'var(--brand-primary)' : 'transparent',
            color: mode === 'dark' ? 'white' : 'var(--text-muted)',
            transition: 'all 0.2s ease'
          }}
        >
          <WeatherMoon16Regular />
        </button>
      </div>
    </div>
  )
}

export function useOrg() {
  return useOutletContext<OrgContext>()
}
